/**
 * One `MM / YY` expiry field, the way Stripe and Adyen collect it. Spreedly's tokenization API
 * wants a 4-digit year ("The 4 digit expiration year of the card"), so the two typed digits are
 * expanded to `20YY` before they leave the browser.
 *
 * @see https://developer.spreedly.com/reference/create-payment-method
 */

export const EXPIRY_SEPARATOR = ' / ';
/** What the field ever displays: `MM / YY`. */
export const EXPIRY_DISPLAY_LENGTH = 2 + EXPIRY_SEPARATOR.length + 2;
/**
 * What the input ATTRIBUTE allows, which is deliberately longer. `maxLength` is enforced by the
 * browser before React sees the value, so capping it at the display length silently truncates a
 * paste or autofill of `01 / 2026` to `01 / 20` — which then parses as January 2020, an expired
 * card. `formatExpiryInput` clamps its own output to the display form, so the wider attribute
 * changes nothing about what the customer ends up seeing; it only lets the longer value arrive
 * intact so the century can be recognised and dropped.
 */
export const EXPIRY_MAX_LENGTH = 'MM / YYYY'.length;

const MAX_MONTH = 12;
const YEAR_PREFIX = '20';

const digitsOnly = (value: string) => value.replace(/\D/g, '');

const dropCentury = (year: string) => (year.length === 4 && year.startsWith(YEAR_PREFIX) ? year.slice(2) : year);

/**
 * A value that already carries a separator — our own `12 / 3`, or a pasted / autofilled `1/2026`,
 * `01-26` — is read as two parts, and each part is normalised on its own: a one-digit month is
 * zero-padded and a four-digit year loses its century. Digit-stripping alone cannot do that:
 * `1/2026` strips to `12026`, which reads as December 2002. Keystroke input has no separator and
 * returns null.
 *
 * Year-first (`2026-12`, what a `month`-type autofill emits) is matched separately: read
 * month-first it would take `20` as the month, fall through the >12 rule and land on February 2006.
 */
function splitOnSeparator(raw: string): string | null {
  const isoMatch = raw.match(/^\s*(20\d{2})\s*[^\d\s]+\s*(\d{1,2})\s*$/);

  if (isoMatch) {
    const month = isoMatch[2].padStart(2, '0');

    return `${month}${dropCentury(isoMatch[1])}`;
  }

  const match = raw.match(/^\s*(\d{1,2})\s*[^\d\s]+\s*(\d{0,4})\s*$/);

  if (!match) {
    return null;
  }

  return `${match[1].padStart(2, '0')}${dropCentury(match[2])}`;
}

/**
 * Formats what the customer has typed so far. Single-digit months above 1 are zero-padded on the
 * spot (`4` → `04 / `), a two-digit month above 12 is read as a one-digit month (`13` → `01 / 3`),
 * and the separator appears as soon as the month is complete — except while the customer is
 * deleting, when the separator is dropped so backspace can reach the month digits.
 *
 * Browser autofill and pasted values may carry a four-digit year and an unpadded month (`12/2026`,
 * `1/2026`): the century is dropped and the month padded so they land as `12 / 26` and `01 / 26`
 * instead of `12 / 20` and `12 / 02`.
 */
export function formatExpiryInput(raw: string, previous = ''): string {
  let digits = splitOnSeparator(raw) ?? digitsOnly(raw);

  // Six digits without a separator (`122026`, `cc-exp` autofill in some browsers): same century rule.
  if (digits.length === 6 && digits.slice(2, 4) === YEAR_PREFIX) {
    digits = `${digits.slice(0, 2)}${digits.slice(4)}`;
  }

  if (digits.length >= 1 && Number(digits[0]) > 1) {
    digits = `0${digits}`;
  }

  if (digits.length >= 2 && Number(digits.slice(0, 2)) > MAX_MONTH) {
    digits = `0${digits}`;
  }

  digits = digits.slice(0, 4);

  const month = digits.slice(0, 2);
  const year = digits.slice(2);
  const deleting = raw.length < previous.length;

  if (digits.length < 2) {
    return digits;
  }

  if (digits.length === 2 && deleting) {
    return month;
  }

  return `${month}${EXPIRY_SEPARATOR}${year}`;
}

export type ExpiryError = 'expiry_invalid' | 'expired';

/** `error` is null exactly when `month`/`year` are set — one shape, so callers branch on `error`. */
export interface ParsedExpiry {
  month: string | null;
  year: string | null;
  error: ExpiryError | null;
}

const invalidExpiry = (error: ExpiryError): ParsedExpiry => ({ month: null, year: null, error });

/** True once four digits are in — the moment to move the cursor on. */
export function isExpiryComplete(value: string): boolean {
  return digitsOnly(value).length === 4;
}

/**
 * `MM / YY` → `{ month: 'MM', year: '20YY' }`. Expiry is checked at month granularity: a card is
 * valid through the last day of its expiry month.
 */
export function parseExpiry(value: string, now: Date = new Date()): ParsedExpiry {
  const digits = digitsOnly(value);

  if (digits.length !== 4) {
    return invalidExpiry('expiry_invalid');
  }

  const month = Number(digits.slice(0, 2));

  if (month < 1 || month > MAX_MONTH) {
    return invalidExpiry('expiry_invalid');
  }

  const year = Number(`${YEAR_PREFIX}${digits.slice(2)}`);
  const current = now.getFullYear() * 100 + (now.getMonth() + 1);

  if (year * 100 + month < current) {
    return invalidExpiry('expired');
  }

  return { month: digits.slice(0, 2), year: String(year), error: null };
}
